import { PlayArrow } from '@mui/icons-material';
import { Button, CardActions, CardContent, Checkbox, FormControl, FormControlLabel, Grid, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { ReactNode, useEffect, useState } from 'react';
import { MarkItViewApi } from '../../../../../Apis/Apis';
import {
  CommodityType,
  ContractDate,
  MarketViewProductModel,
  MarketViewProductTradingMonthsResponse,
  MarketViewVolumeModel,
} from '../../../../../Generated/Mark-It-View-Api';
import { useListResource, useLoadingState, useMtmDate, usePrevious } from '../../../../../Hooks';
import {
  DailyPriceQueryTypeOptions,
  MarkItViewQueryResult as MarkItViewQuery,
  PriceQueryResult,
  PriceQueryType,
  PriceQueryTypeOptions,
} from '../../../../../Types';
import { MarkItViewQueryParameters } from '../../../../../Types/MarkItViewQueryParameters';
import { markItViewQuerySchema } from '../../../../../Validations/queryFormValidation';
import MarkItViewCommodityTypeSelector from '../../../../StoneX/MarkItViewCommodityTypeSelector/MarkItViewCommodityTypeSelector';
import MarkItViewProductSelector from '../../../../StoneX/MarkItViewProductSelector.tsx/MarkItViewProductSelector';
import { StoneXDatePicker, StoneXMonthMultiselect, StoneXMultiselect, StoneXSelect } from '../../../../StoneXMui';
import { StoneXContractMonthPicker } from '../../../../StoneXMui/StoneXContractMonthPicker/StoneXContractMonthPicker';
import './QueryForm.scss';
import UserVolumeModal from './UserVolumeModal';

interface QueryFormProps {
  id: number;
  onUpdateQuery: (query: MarkItViewQuery) => void;
  onUpdateQueryParams: (query: MarkItViewQueryParameters) => void;
  savedQueryToLoad?: MarkItViewQueryParameters;
}

export default function QueryForm(props: QueryFormProps) {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const defaultParams = {
    id: props.id,
    startQuoteDate: props.savedQueryToLoad?.startQuoteDate ?? dayjs(useMtmDate()).subtract(1, 'year').toDate(),
    endQuoteDate: props.savedQueryToLoad?.endQuoteDate ?? useMtmDate(),
    mtmDate: props.savedQueryToLoad?.mtmDate ?? useMtmDate(),
    priceQueryType: props.savedQueryToLoad?.priceQueryType ?? PriceQueryType.Price,
    commodityType: props.savedQueryToLoad?.commodityType ?? CommodityType.Favorites,
    years: props.savedQueryToLoad?.years ?? [],
    months: props.savedQueryToLoad?.months ?? [],
    monthsAhead: props.savedQueryToLoad?.monthsAhead,
    startContractDate: props.savedQueryToLoad?.startContractDate ?? null,
    endContractDate: props.savedQueryToLoad?.endContractDate ?? null,
    useWeightedAverage: props.savedQueryToLoad?.useWeightedAverage ?? false,
    userVolumeId: props.savedQueryToLoad?.userVolumeId ?? null,
    // Re-derived from the product as soon as the selector loads it, but seeded here so a
    // saved query does not briefly validate as a contract-month query on the way in.
    isDailyPriceProduct: props.savedQueryToLoad?.isDailyPriceProduct ?? false,
  } as MarkItViewQueryParameters;

  const [queryParams, setQueryParams] = useState<MarkItViewQueryParameters>(defaultParams);

  const products = useListResource<MarketViewProductModel, number>([], null, (item) => item.productId);

  //Trading Months/Years
  const [tradingMonths, setTradingMonths] = useState<MarketViewProductTradingMonthsResponse>({ years: [], months: [] });
  const tradingMonthsLoadingState = useLoadingState();

  // Saved user volumes (for weighted-average strip queries)
  const [userVolumes, setUserVolumes] = useState<MarketViewVolumeModel[]>([]);
  const userVolumesLoadingState = useLoadingState();
  const [userVolumeModalOpen, setUserVolumeModalOpen] = useState(false);
  const [userVolumeModalEditing, setUserVolumeModalEditing] = useState<MarketViewVolumeModel | null>(null);

  const [queryResult, setQueryResult] = useState<MarkItViewQuery>();
  const previousQueryResult = usePrevious(queryResult);
  const [hasSubmittedOnce, setHasSubmittedOnce] = useState<boolean>(false);
  const [isPerformingFirstLoad, setIsPerformingFirstLoad] = useState(!!props.savedQueryToLoad);
  const [productsUpdating, setProductsUpdating] = useState<{ [key: number]: boolean }>({});

  //report loading state
  const reportLoadingState = useLoadingState();

  const loadSavedQuery = (query: MarkItViewQueryParameters) => {
    // setIsPerformingFirstLoad(true);
    // loadProductTypes()
    //   .then(q => {
    //     commodityTypes.loadingState
    //     const commodityType = commodityTypes.items.find(q => q.commodityType == query.commodityType!);
    // })
    // loadProducts(query.commodityType!).then(() => {
    //   const selectedProduct = products.items.find((q) => q.productId == query.productId!);
    //   if (selectedProduct) {
    //     onProductChange(null, selectedProduct);
    //   }
    // });
  };

  const getClosestYearToCurrent = (years: number[]): number => {
    if (!years || years.length == 0) {
      return 0;
    }

    const currentYear = dayjs().year();
    const closest = years.reduce((a, b) => {
      return Math.abs(b - currentYear) < Math.abs(a - currentYear) ? b : a;
    });
    return closest;
  };

  const loadProductTradingMonths = (productId: number): void => {
    MarkItViewApi.marketViewProductTradingMonths(tradingMonthsLoadingState.setLoadingState, productId.toString()).then((response) => {
      if (response.data) {
        setTradingMonths(response.data);

        if (isPerformingFirstLoad) {
          setIsPerformingFirstLoad(false);
        }
      }
    });
  };

  const loadUserVolumes = (): Promise<MarketViewVolumeModel[]> => {
    return MarkItViewApi.listMarketViewVolumes(userVolumesLoadingState.setLoadingState).then((response) => {
      const rows = response.data?.rows ?? [];
      setUserVolumes(rows);
      return rows;
    });
  };

  const supportsWeightedAverage = (type: PriceQueryType | null | undefined): boolean =>
    type === PriceQueryType.StripAverage || type === PriceQueryType.CalendarStripAverage;

  // Build default contract-month rows for the user volume modal based on the current strip selection.
  const buildDefaultModalMonths = (): ContractDate[] => {
    if (queryParams.priceQueryType === PriceQueryType.StripAverage) {
      const start = queryParams.startContractDate;
      const end = queryParams.endContractDate;
      if (start?.year == null || start?.month == null || end?.year == null || end?.month == null) {
        return [];
      }
      const months: ContractDate[] = [];
      let y = start.year;
      let m = start.month; // 0-indexed
      // Walk inclusive from start to end.
      while (y < end.year || (y === end.year && m <= end.month)) {
        months.push({ year: y, month: m });
        m++;
        if (m > 11) {
          m = 0;
          y++;
        }
      }
      return months;
    }

    if (queryParams.priceQueryType === PriceQueryType.CalendarStripAverage) {
      const years = queryParams.years ?? [];
      const months: ContractDate[] = [];
      for (const y of years) {
        for (let m = 0; m < 12; m++) {
          months.push({ year: y, month: m });
        }
      }
      return months;
    }

    return [];
  };

  const onWeightedAverageToggle = (checked: boolean) => {
    setQueryParams((prev) => ({
      ...prev,
      useWeightedAverage: checked,
      userVolumeId: checked ? prev.userVolumeId ?? null : null,
    }));
    if (checked && userVolumes.length === 0 && !userVolumesLoadingState.isLoading()) {
      loadUserVolumes();
    }
  };

  const onUserVolumeChange = (userVolumeId: number | null) => {
    setQueryParams((prev) => ({ ...prev, userVolumeId }));
    if (userVolumeId != null) {
      setErrors((prev) => {
        if (!prev.userVolumeId) return prev;
        const { userVolumeId: _omit, ...rest } = prev;
        return rest;
      });
    }
  };

  const openAddUserVolumeModal = () => {
    setUserVolumeModalEditing(null);
    setUserVolumeModalOpen(true);
  };

  const openEditUserVolumeModal = () => {
    const selected = userVolumes.find((v) => v.userVolumeId === queryParams.userVolumeId);
    if (!selected) return;
    setUserVolumeModalEditing(selected);
    setUserVolumeModalOpen(true);
  };

  const onUserVolumeSaved = (savedId?: number) => {
    setUserVolumeModalOpen(false);
    loadUserVolumes().then(() => {
      if (savedId != null) {
        setQueryParams((prev) => ({ ...prev, userVolumeId: savedId }));
      }
    });
  };

  const onQueryParamsUpdate = () => {
    validateQuery(queryParams);
  };

  useEffect(() => {
    if (!isPerformingFirstLoad && props.savedQueryToLoad) {
      submit();
    }
  }, [isPerformingFirstLoad]);

  useEffect(() => {
    if (hasSubmittedOnce) {
      validateQuery(queryParams);
    }
    props.onUpdateQueryParams(queryParams);
  }, [queryParams]);

  const onStartQuoteDateChange = (value: Date | null): void => setQueryParams({ ...queryParams, startQuoteDate: value });
  const onEndQuoteDateChange = (value: Date | null): void => setQueryParams({ ...queryParams, endQuoteDate: value });
  const onMtmDateChange = (value: Date | null): void => setQueryParams({ ...queryParams, mtmDate: value });
  const onPriceQueryTypeChange = (x: PriceQueryType | null): void => {
    setQueryParams((prev) => {
      const next: MarkItViewQueryParameters = { ...prev, priceQueryType: x };
      if (!supportsWeightedAverage(x)) {
        next.useWeightedAverage = false;
        next.userVolumeId = null;
      }
      return next;
    });
  };
  const onYearsChange = (value: number[]): void => setQueryParams({ ...queryParams, years: value });
  const onMonthsChange = (value: number[]): void => setQueryParams({ ...queryParams, months: value });
  // Both month pickers normalize a loaded 1-based month to 0-based in their mount effect, so these two updates
  // can land in the same render. Functional updates keep the first from being overwritten by the second.
  const onStartContractDateChange = (value: ContractDate | null): void =>
    setQueryParams((previous) => ({ ...previous, startContractDate: value }));
  const onEndContractDateChange = (value: ContractDate | null): void =>
    setQueryParams((previous) => ({ ...previous, endContractDate: value }));

  const onCommodityTypeChange = (commodityType: CommodityType | null) => {
    setQueryParams({ ...queryParams, commodityType: commodityType });

    if (commodityType === null) {
      // Also clear the selected product.
      setQueryParams({ ...queryParams, product: null, productId: null });
    } else {
      // loadProducts(commodityType);
    }
  };

  const onProductChange = (product: MarketViewProductModel | null) => {
    if (product === null) {
      // products.clearSelected();
      setTradingMonths({});
      tradingMonthsLoadingState.reset();
      setQueryParams({ ...queryParams, productId: null, product: null, isDailyPriceProduct: false });
      products.setSelectedItem(null);
    } else {
      const isDailyPriceProduct = product.hasDailyPrices;

      setQueryParams({
        ...queryParams,
        productId: product.productId,
        product: product,
        isDailyPriceProduct,
        // A daily assessment has one price per day and no contract month, so Price is the
        // only query it can answer, and the year and month selections have nothing to
        // choose from. Clearing them keeps a stale selection from a previous product out
        // of the request.
        ...(isDailyPriceProduct
          ? { priceQueryType: PriceQueryType.Price, years: [], months: [], useWeightedAverage: false, userVolumeId: null }
          : {}),
      });

      if (isDailyPriceProduct) {
        // There are no trading months to load - the product has no contract months.
        setTradingMonths({ years: [], months: [] });
        tradingMonthsLoadingState.reset();
      } else {
        loadProductTradingMonths(product.productId);
      }

      products.setSelectedItem(product);
    }
    // onQueryParamsUpdate();
  };

  const toggleFavorite = (product: MarketViewProductModel): void => {
    product.isFavorite = !product.isFavorite;

    const callback = () => {
      const newSet = [...products.items.filter((q) => q.productId != product.productId), product];
      products.setItems(newSet);
      setProductsUpdating((previous) => ({ ...previous, [product.productId]: false }));
    };

    const savedFailedCallback = () => {
      setProductsUpdating((previous) => ({ ...previous, [product.productId]: false }));
    };

    if (product.isFavorite) {
      setProductsUpdating((previous) => ({ ...previous, [product.productId]: true }));
      MarkItViewApi.addMarketViewUserFavoritedProduct(null, String(product.productId)).then(callback).catch(savedFailedCallback);
    } else {
      setProductsUpdating((previous) => ({ ...previous, [product.productId]: true }));
      MarkItViewApi.deleteMarketViewUserFavoritedProduct(null, String(product.productId)).then(callback).catch(savedFailedCallback);
    }
  };

  const onMonthsAheadChange = (event: any) => {
    const isNumber = Number.isFinite(parseInt(event.target.value));
    const monthsAhead = isNumber ? parseInt(event.target.value) : null;
    setQueryParams({ ...queryParams, monthsAhead: monthsAhead });
  };

  const validateQuery = (query: MarkItViewQueryParameters): boolean => {
    const validationErrors: { [key: string]: string } = {};

    try {
      markItViewQuerySchema.validateSync(query, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err: any) {
      err.inner.forEach((e: any) => {
        validationErrors[e.path] = e.message;
      });

      setErrors(validationErrors);

      return false;
    }

    // markItViewQuerySchema
    //   .validate(query, { abortEarly: false })
    //   .then((q) => {
    //     setErrors({});
    //   })
    //   .catch((err) => {
    //     err.inner.forEach((e: any) => {
    //       validationErrors[e.path] = e.message;
    //     });

    //     if (hasSubmittedOnce) {
    //       setErrors(validationErrors);
    //     }

    //     return false;
    //   });

    // return Object.keys(validationErrors).length === 0;
  };

  const submit = (): void => {
    setHasSubmittedOnce(true);

    if (!validateQuery(queryParams)) {
      return;
    }

    if (
      queryParams.useWeightedAverage &&
      supportsWeightedAverage(queryParams.priceQueryType) &&
      (queryParams.userVolumeId == null ||
        !userVolumes.some((v) => v.userVolumeId === queryParams.userVolumeId))
    ) {
      setErrors((prev) => ({
        ...prev,
        userVolumeId: 'Select a user volume to run a weighted average query',
      }));
      return;
    }

    if (queryParams.isDailyPriceProduct) {
      getDailyPriceQuery();
      return;
    }

    switch (queryParams.priceQueryType) {
      case PriceQueryType.Price:
        getPriceQuery();
        break;
      case PriceQueryType.StripAverage:
        getStripAverage();
        break;
      case PriceQueryType.CalendarStripAverage:
        getCalendarStripAverage();
        break;
      case PriceQueryType.RollingMonthAhead:
        getRollingMonthAhead();
        break;
      case PriceQueryType.Rolling12MonthStrip:
        getRolling12MonthAheadStrip();
        break;
      case PriceQueryType.ForwardCurve:
        getForwardCurve();
        break;
      case PriceQueryType.Settlement:
        getSettlementPrices();
        break;
    }
  };

  const getPriceQuery = () => {
    const startQuoteDateString = dayjs(queryParams.startQuoteDate).format('L');
    const endQuoteDateString = dayjs(queryParams.endQuoteDate).format('L');

    MarkItViewApi.listMarketViewPrices(
      reportLoadingState.setLoadingState,
      String(queryParams.productId!),
      queryParams.years!,
      queryParams.months!,
      startQuoteDateString,
      endQuoteDateString,
    ).then((response) => {
      onQueryUpdate(PriceQueryType.Price, queryParams.product!, response.data);
    });

    onQueryUpdate(PriceQueryType.Price, queryParams.product!);
  };

  const getDailyPriceQuery = () => {
    // The endpoint takes the quote date range as an inclusive filter and returns one row
    // per day, so the start and end quote dates are the whole query.
    const startQuoteDateString = dayjs(queryParams.startQuoteDate).format('YYYY-MM-DD');
    const endQuoteDateString = dayjs(queryParams.endQuoteDate).format('YYYY-MM-DD');

    MarkItViewApi.listMarketViewDailyPrices(
      reportLoadingState.setLoadingState,
      String(queryParams.productId!),
      startQuoteDateString,
      endQuoteDateString,
    ).then((response) => {
      onQueryUpdate(PriceQueryType.DailyPrice, queryParams.product!, response.data);
    });

    onQueryUpdate(PriceQueryType.DailyPrice, queryParams.product!);
  };

  const getStripAverage = () => {
    const startQuoteDateString = dayjs(queryParams.startQuoteDate).format('L');
    const endQuoteDateString = dayjs(queryParams.endQuoteDate).format('L');

    MarkItViewApi.listMarketViewPricesStripAverage(
      reportLoadingState.setLoadingState,
      String(queryParams.productId!),
      startQuoteDateString,
      endQuoteDateString,
      queryParams.startContractDate!.year!,
      queryParams.startContractDate!.month! + 1,
      queryParams.endContractDate!.year!,
      queryParams.endContractDate!.month! + 1,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      queryParams.useWeightedAverage ? (queryParams.userVolumeId ?? undefined) : undefined,
    ).then((response) => onQueryUpdate(PriceQueryType.StripAverage, queryParams.product!, response.data));

    onQueryUpdate(PriceQueryType.StripAverage, queryParams.product!);
  };

  const getCalendarStripAverage = () => {
    const startQuoteDateString = dayjs(queryParams.startQuoteDate).format('L');
    const endQuoteDateString = dayjs(queryParams.endQuoteDate).format('L');

    MarkItViewApi.listMarketViewPriceQueryCalendarStripAvearge(
      reportLoadingState.setLoadingState,
      String(queryParams.productId!),
      queryParams.years!,
      startQuoteDateString,
      endQuoteDateString,
      queryParams.useWeightedAverage ? (queryParams.userVolumeId ?? undefined) : undefined,
    ).then((response) => onQueryUpdate(PriceQueryType.CalendarStripAverage, queryParams.product!, response.data));

    onQueryUpdate(PriceQueryType.CalendarStripAverage, queryParams.product!);
  };

  const getRollingMonthAhead = () => {
    const startQuoteDateString = dayjs(queryParams.startQuoteDate).format('L');
    const endQuoteDateString = dayjs(queryParams.endQuoteDate).format('L');
    const rollingMonths = 1;

    MarkItViewApi.listMarketViewPriceQueryRollingMonthAhead(
      reportLoadingState.setLoadingState,
      String(queryParams.productId!),
      rollingMonths,
      startQuoteDateString,
      endQuoteDateString,
    ).then((response) => onQueryUpdate(PriceQueryType.RollingMonthAhead, queryParams.product!, response.data));

    onQueryUpdate(PriceQueryType.RollingMonthAhead, queryParams.product!);
  };

  const getRolling12MonthAheadStrip = () => {
    const startQuoteDateString = dayjs(queryParams.startQuoteDate).format('L');
    const endQuoteDateString = dayjs(queryParams.endQuoteDate).format('L');
    const rollingMonths = 12;

    MarkItViewApi.listMarketViewPriceQueryRollingMonthAhead(
      reportLoadingState.setLoadingState,
      String(queryParams.productId!),
      rollingMonths,
      startQuoteDateString,
      endQuoteDateString,
    ).then((response) => onQueryUpdate(PriceQueryType.Rolling12MonthStrip, queryParams.product!, response.data));

    onQueryUpdate(PriceQueryType.Rolling12MonthStrip, queryParams.product!);
  };

  const getForwardCurve = () => {
    const mtmDateString = dayjs(queryParams.endQuoteDate).format('L');

    MarkItViewApi.listMarketViewForwardCurveQuery(
      reportLoadingState.setLoadingState,
      String(queryParams.productId!),
      mtmDateString,
      queryParams.monthsAhead ?? undefined,
    ).then((response) => onQueryUpdate(PriceQueryType.ForwardCurve, queryParams.product!, response.data));

    onQueryUpdate(PriceQueryType.ForwardCurve, queryParams.product!);
  };

  const getSettlementPrices = () => {
    const startQuoteDateString = dayjs(queryParams.startQuoteDate).format('L');
    const endQuoteDateString = dayjs(queryParams.endQuoteDate).format('L');

    MarkItViewApi.listMarketViewSettlementPrices(
      reportLoadingState.setLoadingState,
      String(queryParams.productId!),
      startQuoteDateString,
      endQuoteDateString,
    ).then((response) => onQueryUpdate(PriceQueryType.Settlement, queryParams.product!, response.data));

    onQueryUpdate(PriceQueryType.ForwardCurve, queryParams.product!);
  }

  const onQueryUpdate = (queryType: PriceQueryType, product: MarketViewProductModel, prices?: PriceQueryResult) => {
    const query = {
      id: props.id,
      product: product,
      query: prices,
      queryType: queryType,
      hidden: false,
      loadingState: reportLoadingState,
    } as MarkItViewQuery;

    setQueryResult(query);
  };

  useEffect(() => {
    if (!queryResult) return;
    const updatedQuery = { ...queryResult, loadingState: reportLoadingState } as MarkItViewQuery;
    setQueryResult(updatedQuery);
  }, [reportLoadingState.loadingState]);

  useEffect(() => {
    if (!previousQueryResult && !queryResult) return;

    props.onUpdateQuery(queryResult!);
  }, [queryResult]);

  const renderPriceTypeMonthInput = (): ReactNode => {
    // One price per day and no contract month, so there is nothing left to narrow by.
    if (queryParams.isDailyPriceProduct) {
      return <></>;
    }

    switch (queryParams.priceQueryType) {
      case PriceQueryType.Price:
        return priceQueryInput();
      case PriceQueryType.StripAverage:
        return <>{stripAverageInput()}{weightedAverageInput()}</>;
      case PriceQueryType.CalendarStripAverage:
        return <>{calendarStripAverageInput()}{weightedAverageInput()}</>;
      case PriceQueryType.ForwardCurve:
        return forwardCurveInput();
      case PriceQueryType.Settlement:
        return <></>;
      default:
        return <></>;
    }
  };

  const priceQueryInput = (): ReactNode => {
    return (
      <Grid container item direction="column" xs="auto" spacing={2}>
        <Grid item>
          <StoneXMultiselect
            list={tradingMonths!.years!}
            labelSelector={(q) => q}
            valueSelector={(q) => q}
            loading={tradingMonthsLoadingState.isLoading}
            resultSetter={onYearsChange}
            initial={queryParams.years}
            autoFocusValue={getClosestYearToCurrent(tradingMonths!.years!)}
            error={errors['years']}
            label="Years"
          />
        </Grid>
        <Grid item>
          <StoneXMonthMultiselect
            allowedMonths={tradingMonths!.months!}
            initial={queryParams.months}
            resultSetter={onMonthsChange}
            error={errors['months']}
          ></StoneXMonthMultiselect>
        </Grid>
      </Grid>
    );
  };

  const stripAverageInput = (): ReactNode => {
    return (
      <Grid container item direction="column" xs="auto" spacing={2}>
        <Grid item>
          <StoneXContractMonthPicker
            value={queryParams.startContractDate}
            onChange={onStartContractDateChange}
            label="Start Month"
            error={errors['startContractDate']}
          />
        </Grid>
        <Grid item>
          <StoneXContractMonthPicker
            value={queryParams.endContractDate}
            onChange={onEndContractDateChange}
            label="End Month"
            error={errors['endContractDate']}
          />
        </Grid>
      </Grid>
    );
  };

  const calendarStripAverageInput = (): ReactNode => {
    return (
      <Grid container item direction="column" xs="auto" spacing={2}>
        <Grid item>
          <StoneXMultiselect
            list={tradingMonths!.years!}
            labelSelector={(q) => q}
            valueSelector={(q) => q}
            loading={tradingMonthsLoadingState.isLoading}
            resultSetter={onYearsChange}
            initial={queryParams.years}
            autoFocusValue={getClosestYearToCurrent(tradingMonths!.years!)}
            error={errors['years']}
            label="Years"
          />
        </Grid>
      </Grid>
    );
  };

  const forwardCurveInput = (): ReactNode => {
    return (
      <Grid container item direction="column" xs="auto" spacing={2}>
        <Grid item>
          <FormControl sx={{ width: 300 }}>
            <TextField label="Months Ahead" type="number" onChange={onMonthsAheadChange} />
          </FormControl>
        </Grid>
      </Grid>
    );
  };

  const weightedAverageInput = (): ReactNode => {
    const selectedExists = queryParams.userVolumeId != null && userVolumes.some((v) => v.userVolumeId === queryParams.userVolumeId);

    return (
      <Grid container item direction="column" xs="auto" spacing={2}>
        <Grid item>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!queryParams.useWeightedAverage}
                onChange={(e) => onWeightedAverageToggle(e.target.checked)}
              />
            }
            label="Run as Weighted Average"
          />
        </Grid>
        {queryParams.useWeightedAverage && (
          <>
            <Grid item>
              <StoneXSelect
                options={userVolumes.map((v) => v.userVolumeId)}
                getId={(x) => x}
                getOptionLabel={(x) => userVolumes.find((v) => v.userVolumeId === x)?.name ?? ''}
                value={selectedExists ? queryParams.userVolumeId! : null}
                label="User Volume"
                onChange={(id) => onUserVolumeChange(id ?? null)}
                error={errors.userVolumeId}
              />
            </Grid>
            <Grid item>
              <Button size="small" onClick={openAddUserVolumeModal}>Add</Button>
              <Button size="small" onClick={openEditUserVolumeModal} disabled={!selectedExists} sx={{ ml: 1 }}>Edit</Button>
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  const renderDatePickers = (): ReactNode => {
    if (queryParams.priceQueryType === PriceQueryType.ForwardCurve) {
      return (
        <Grid container direction="column" xs="auto" item spacing={2}>
          <Grid item>
            <StoneXDatePicker value={queryParams.mtmDate!} onChange={onMtmDateChange} label="MTM Date" />
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container direction="column" xs="auto" item spacing={2}>
        <Grid item>
          <StoneXDatePicker value={queryParams.startQuoteDate!} onChange={onStartQuoteDateChange} label="Start Quote Date" />
        </Grid>
        <Grid item>
          <StoneXDatePicker value={queryParams.endQuoteDate!} onChange={onEndQuoteDateChange} label="End Quote Date" />
        </Grid>
      </Grid>
    );
  };

  useEffect(() => {
    if (props.savedQueryToLoad) {
      // setIsPerformingFirstLoad(true);
      // loadSavedQuery(props.savedQueryToLoad);
    }
  }, []);

  useEffect(() => {
    if (supportsWeightedAverage(queryParams.priceQueryType) && queryParams.useWeightedAverage && userVolumes.length === 0 && !userVolumesLoadingState.isLoading()) {
      loadUserVolumes();
    }
  }, [queryParams.priceQueryType, queryParams.useWeightedAverage]);

  return (
    <>
      <CardContent sx={{ flexGrow: 1, padding: '40px'}}>
        <Grid container spacing={2}>
          {renderDatePickers()}
          <Grid container direction="column" xs="auto" item spacing={2}>
            <Grid item>
              <StoneXSelect
                options={queryParams.isDailyPriceProduct ? DailyPriceQueryTypeOptions : PriceQueryTypeOptions}
                getId={x => x}
                value={queryParams.priceQueryType}
                label="Price Query Type"
                error={errors['priceQueryType']}
                onChange={onPriceQueryTypeChange}
                getOptionLabel={x => x}
                isControlled
              />
            </Grid>
          </Grid>
          <Grid container direction="column" xs="auto" item spacing={2}>
            <Grid item>
              <MarkItViewCommodityTypeSelector
                value={queryParams.commodityType}
                error={errors['commodityType']}
                onChange={onCommodityTypeChange}
              />
            </Grid>
            <Grid item>
              <MarkItViewProductSelector 
                commodityType={queryParams.commodityType} 
                initialProductId={props.savedQueryToLoad?.productId}
                lastQuoteDateThresholdDays={45}
                onProductChange={onProductChange}
                errors={errors['productId']}
              />
            </Grid>
          </Grid>
          {renderPriceTypeMonthInput()}
        </Grid>
      </CardContent>

      <CardActions sx={{paddingLeft: '40px'}}>
        <Button onClick={submit} startIcon={<PlayArrow />}>
          Run Query
        </Button>
      </CardActions>

      <UserVolumeModal
        open={userVolumeModalOpen}
        onClose={() => setUserVolumeModalOpen(false)}
        onSaved={onUserVolumeSaved}
        volume={userVolumeModalEditing}
        defaultMonths={userVolumeModalEditing ? undefined : buildDefaultModalMonths()}
      />
    </>
  );
}
